import React from 'react'
import _ from 'lodash'
import { cn } from '../../lib/utils'
import { useNav } from './NavProvider'
import { Bar, SplitBar } from './icons'

const SubNav = React.forwardRef(
  (
    {
      className,
      parentActive,
      theme,
      items,
      id,
      'aria-hidden': ariaHidden,
      onMenuItemKeyDown,
    },
    ref,
  ) => {
    const subNavClasses = cn('flex flex-col overflow-y-hidden mx-4', className)
    const { activeSubNav } = useNav()

    if (!items) {
      return null
    }
    const activeIndex = items.findIndex((child) => child.slug === activeSubNav)
    const lastIndex = items.length - 1
    return (
      <div
        ref={ref}
        id={id}
        role="menu"
        aria-hidden={ariaHidden}
        className={subNavClasses}
      >
        {items.map((child, childIndex) => (
          <SubNavLink
            key={childIndex}
            item={child}
            theme={theme}
            parentActive={parentActive}
            withDecoration
            isLast={childIndex === lastIndex}
            activeBefore={activeIndex <= childIndex}
            onMenuItemKeyDown={onMenuItemKeyDown}
          />
        ))}
      </div>
    )
  },
)
SubNav.displayName = 'SubNav'
export default SubNav

export const SubNavLink = ({
  withDecoration,
  theme,
  activeBefore,
  parentActive: _parentActive,
  isLast,
  item,
  onMenuItemKeyDown,
}) => {
  const {
    activeSubNav,
    setActiveSubNav,
    navigateTo,
    meetsMinimumProgramLevel,
    hasProgramLevel,
    candidateBelongsToPartnerCompany,
    partnerHasUpcomingEvents,
    clientAdminProfileRole,
  } = useNav()

  const { title, slug, programLevel, exactProgramLevel, data, advantageRole } =
    item

  const advantageRoles = Array.isArray(advantageRole)
    ? advantageRole
    : [advantageRole].filter(Boolean)

  if (
    !_.isEmpty(advantageRoles) &&
    !advantageRoles.includes(clientAdminProfileRole)
  ) {
    return null
  }

  const inactiveBaseClasses =
    'px-3 py-2 tracking-tight text-sm w-full focus:text-foreground focus:outline-none hover:bg-cod-gray-100 hover:rounded text-cod-gray-800 focus:bg-cod-gray-200 focus:text-cod-gray-800'
  let inactiveThemeClasses = 'hover:text-intoo-blue-medium'
  switch (theme) {
    case 'green':
      inactiveThemeClasses = 'hover:text-foreground'
      break
    case 'deloitte':
      inactiveThemeClasses =
        'hover:text-white hover:bg-deloitte focus:bg-deloitte focus:text-white'
      break
    case 'advantage':
      inactiveThemeClasses =
        'hover:bg-[#E0EFFE] focus:bg-[#E0EFFE] focus:text-cod-gray-800'
  }
  const inactiveClasses = cn(inactiveBaseClasses, inactiveThemeClasses)
  let activeThemeClasses = 'bg-[#E0F2F9]'
  switch (theme) {
    case 'green':
      activeThemeClasses =
        'bg-[#c8e1b5] text-cod-gray-800 hover:text-foreground'
      break
    case 'deloitte':
      activeThemeClasses =
        'bg-black text-white hover:text-white hover:bg-deloitte focus:bg-deloitte'
  }
  const activeClasses = cn(
    inactiveClasses,
    activeThemeClasses,
    'rounded font-medium',
  )
  const active = slug === activeSubNav
  const activate = () => {
    setActiveSubNav(slug)
    navigateTo(item)
  }
  const handleKeyDown = (e) => {
    onMenuItemKeyDown?.(e)
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      activate()
    }
  }
  const defaultDecorationClasses =
    'z-[-1] relative -top-4 left-4 h-9 w-5 rounded-bl-lg border-b-2 border-l-2 border-solid border-cod-gray-200'
  let themeBorder = 'border-[#E0F2F9]'
  let themeBorderActive = ''
  switch (theme) {
    case 'green':
      themeBorder = 'border-intoo-green-coachcta'
      break
    case 'deloitte':
      themeBorder = 'border-black'
      themeBorderActive = 'border-deloitte'
  }
  const decorationClasses = active
    ? cn(defaultDecorationClasses, themeBorder)
    : cn(defaultDecorationClasses, themeBorderActive)
  const barClasses = cn(
    'relative -left-[2px]',
    isLast && 'hidden',
    active && 'top-[7px]',
  )
  const BarIcon = active ? SplitBar : Bar

  const linkProps = {}
  if (!_.isEmpty(data)) {
    Object.entries(data).forEach(([key, value]) => {
      linkProps[`data-${key}`] = value
    })
  }
  if (!_.isEmpty(item.path)) {
    linkProps.href = item.path
  } else if (!_.isEmpty(item.externalLink)) {
    linkProps.href = item.externalLink
    linkProps.target = '_blank'
    linkProps.rel = 'noopener noreferrer'
  } else {
    linkProps.onClick = activate
  }

  if (item.hideFromPartnerCompanies && candidateBelongsToPartnerCompany) {
    return null
  }
  if (
    item.showIfUpcomingEvents &&
    candidateBelongsToPartnerCompany &&
    !partnerHasUpcomingEvents
  ) {
    return null
  }
  if (!meetsMinimumProgramLevel(programLevel)) {
    return null
  }
  if (!_.isEmpty(exactProgramLevel) && !hasProgramLevel(exactProgramLevel)) {
    return null
  }
  return (
    <div className="relative flex cursor-pointer flex-row items-center">
      {withDecoration && (
        <div className="z-0 w-10">
          <div className={decorationClasses}>
            {!isLast && (
              <BarIcon
                theme={theme}
                isColored={!activeBefore}
                className={barClasses}
              />
            )}
          </div>
        </div>
      )}
      <a
        onKeyDown={handleKeyDown}
        role="menuitem"
        tabIndex={-1}
        className={active ? activeClasses : inactiveClasses}
        {...linkProps}
      >
        {title}
      </a>
    </div>
  )
}
